import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Check, Clock, AlertTriangle, Sparkles, Users, UserPlus, X, Heart } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle } from "@/components/Brutal";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const euro = (c) => "€ " + ((c || 0) / 100).toFixed(2).replace(".", ",");

const STATUS_META = {
  proef: { label: "Gratis proefperiode", color: "bg-brand-yellow", icon: Clock },
  actief: { label: "Actieve licentie", color: "bg-brand-green", icon: Check },
  verlopen: { label: "Verlopen", color: "bg-brand-pink", icon: AlertTriangle },
  beheer: { label: "Beheerder (onbeperkt)", color: "bg-brand-blue text-white", icon: Crown },
  vrij: { label: "Onbeperkt", color: "bg-brand-green", icon: Check },
  friends_family: { label: "Friends & Family (onbeperkt)", color: "bg-brand-green", icon: Heart },
};

export default function Abonnement() {
  const navigate = useNavigate();
  const t = useT();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const [familyOn, setFamilyOn] = useState({}); // plan -> bool
  const [assignId, setAssignId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);

  const load = async () => setSub((await api.get("/subscription")).data);
  useEffect(() => { load(); }, []);

  const buy = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Betaling kon niet worden gestart"));
      setBusy(null);
    }
  };

  const assign = async () => {
    if (!assignId) return;
    setAssignBusy(true);
    try {
      const { data } = await api.post("/family-bundle/assign", { user_id: assignId });
      setSub((s) => ({ ...s, family_bundle: data }));
      setAssignId("");
      toast.success(t("Toegevoegd aan je Family Bundel 🎉"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
    finally { setAssignBusy(false); }
  };

  const removeMember = async (uid) => {
    try {
      const { data } = await api.post("/family-bundle/remove", { user_id: uid });
      setSub((s) => ({ ...s, family_bundle: data }));
      toast.success(t("Verwijderd uit je bundel"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  if (!sub) return <div className="text-center py-20"><p className="font-head font-black text-xl animate-pulse">{t("Laden…")}</p></div>;

  const meta = STATUS_META[sub.status] || STATUS_META.proef;
  const StatusIcon = meta.icon;
  const showPlans = ["proef", "verlopen", "actief"].includes(sub.status) && sub.paywall_enabled;
  const usage = sub.usage || {}; const limits = sub.limits || {};
  const bundle = sub.family_bundle || {};

  const savingPct = (p) => {
    const full = (p.price || 0) * (p.family_seats || 6);
    if (!full || !p.family_price) return 0;
    return Math.round((1 - p.family_price / full) * 100);
  };

  const bar = (used, max) => {
    const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
    const full = max && used >= max;
    return (
      <div className="w-full">
        <div className="flex justify-between text-xs font-bold mb-1"><span></span><span className={full ? "text-brand-pink" : ""}>{used} / {max}</span></div>
        <div className="h-2.5 rounded-full bg-brand-ink/10 overflow-hidden"><div className={`h-full ${full ? "bg-brand-pink" : "bg-brand-green"}`} style={{ width: `${pct}%` }} /></div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="abonnement-page">
      <button onClick={() => navigate("/meer")} className="flex items-center gap-1.5 font-bold text-sm text-brand-ink/60"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />{t("Meer")}</button>
      <SectionTitle sub={t("Je proefperiode, licentie & upgrades")}>{t("Mijn abonnement")}</SectionTitle>

      <BrutalCard data-testid="sub-status-card" className={meta.color}>
        <div className="flex items-center gap-3">
          <div className="brutal-sm rounded-full bg-white/40 p-2"><StatusIcon className="w-6 h-6" strokeWidth={2.5} /></div>
          <div className="flex-1">
            <p className="font-head font-black text-lg" data-testid="sub-status">{t(meta.label)}</p>
            {sub.status === "proef" && <p className="text-sm font-semibold">{t("Nog")} {sub.days_left} {t("dagen gratis")}</p>}
            {sub.status === "actief" && <p className="text-sm font-semibold">{t("Licentie loopt af over")} {sub.days_left} {t("dagen")}</p>}
            {sub.status === "verlopen" && <p className="text-sm font-semibold">{t("Koop een licentie om weer items aan te maken")}</p>}
          </div>
        </div>
      </BrutalCard>

      {sub.status === "proef" && (
        <BrutalCard data-testid="usage-card">
          <p className="font-head font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" strokeWidth={2.5} />{t("Gebruik tijdens proef")}</p>
          <div className="space-y-3">
            <div><p className="text-sm font-bold mb-1">{t("Taken")}</p>{bar(usage.tasks, limits.tasks)}</div>
            <div><p className="text-sm font-bold mb-1">{t("Recepten")}</p>{bar(usage.recipes, limits.recipes)}</div>
            <div><p className="text-sm font-bold mb-1">{t("Overige items")}</p>{bar(usage.content, limits.content)}</div>
            <p className="text-xs text-brand-ink/60 font-semibold">{t("AI-functies (Wizzy) zijn onbeperkt beschikbaar tijdens je proefperiode. 🎉")}</p>
          </div>
        </BrutalCard>
      )}

      {bundle.active && (
        <BrutalCard data-testid="family-bundle-manage" className="bg-brand-violet text-white">
          <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5" strokeWidth={2.5} /><p className="font-head font-black text-lg">{t("Jouw Family Bundel")}</p></div>
          <p className="text-sm font-semibold text-white/80">{bundle.plan_label} · {bundle.seats - bundle.seats_left}/{bundle.seats} {t("plekken gebruikt")}</p>
          <div className="mt-3 space-y-2">
            {bundle.members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2 bg-white/15 rounded-xl px-3 py-2" data-testid={`family-member-${m.user_id}`}>
                <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-head font-black overflow-hidden shrink-0">
                  {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover" /> : (m.name?.[0]?.toUpperCase() || "?")}
                </div>
                <span className="flex-1 font-bold text-sm truncate">{m.name}{m.is_owner && ` ${t("(jij)")}`}</span>
                {!m.is_owner && (
                  <button data-testid={`family-remove-${m.user_id}`} onClick={() => removeMember(m.user_id)} className="rounded-full bg-white/20 hover:bg-white/30 p-1.5"><X className="w-4 h-4" strokeWidth={2.5} /></button>
                )}
              </div>
            ))}
          </div>
          {bundle.seats_left > 0 ? (
            <div className="mt-3 flex gap-2">
              <select data-testid="family-assign-select" value={assignId} onChange={(e) => setAssignId(e.target.value)}
                className="flex-1 rounded-xl px-3 py-2.5 text-sm font-bold text-brand-ink outline-none">
                <option value="">{t("Kies een huisgenoot…")}</option>
                {(bundle.assignable || []).map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
              </select>
              <BrutalButton data-testid="family-assign-btn" variant="green" size="sm" disabled={!assignId || assignBusy} onClick={assign}>
                <UserPlus className="w-4 h-4" strokeWidth={2.5} />{t("Toevoegen")}
              </BrutalButton>
            </div>
          ) : (
            <p className="mt-3 text-xs font-semibold text-white/80">{t("Je bundel zit vol. Extra mensen kunnen een regulier abonnement kiezen.")}</p>
          )}
          {bundle.seats_left > 0 && (bundle.assignable || []).length === 0 && (
            <p className="mt-2 text-xs font-semibold text-white/70">{t("Nog geen huisgenoten om toe te voegen. Nodig eerst mensen uit in je organisatie.")}</p>
          )}
        </BrutalCard>
      )}

      {showPlans && (
        <div className="space-y-3">
          <p className="font-head font-black text-lg">{t("Kies een licentie")}</p>
          {sub.plans.map((p) => {
            const fam = !!familyOn[p.plan];
            const activePlan = fam ? p.family_plan : p.plan;
            const price = fam ? p.family_price : p.price;
            const pct = savingPct(p);
            return (
              <BrutalCard key={p.plan} data-testid={`plan-${p.plan}`} className={fam ? "bg-brand-violet/5 border-brand-violet" : ""}>
                {fam && (
                  <div data-testid={`promo-banner-${p.plan}`} className="brutal-sm rounded-xl bg-brand-violet text-white px-3 py-2 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                    <span className="text-sm font-bold">Family Bundel — tot {p.family_seats} personen{pct > 0 ? ` · bespaar ${pct}%` : ""}. Snel iedereen aan boord! 🎉</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-head font-bold text-lg">{fam ? p.family_label : p.label}</p>
                    <p className="text-sm text-brand-ink/60 font-semibold">{fam ? `${t("Onbeperkt voor tot")} ${p.family_seats} ${t("personen")}` : t("Onbeperkt gebruik van alle functies")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-head font-black text-xl">{euro(price)}</p>
                    {fam && <p className="text-[11px] text-brand-ink/50 font-semibold line-through">{euro((p.price || 0) * (p.family_seats || 6))}</p>}
                  </div>
                  <BrutalButton data-testid={`buy-${p.plan}`} variant="green" size="sm" disabled={busy} onClick={() => buy(activePlan)}>
                    {busy === activePlan ? "…" : t("Kopen")}
                  </BrutalButton>
                </div>
                <button data-testid={`family-toggle-${p.plan}`} onClick={() => setFamilyOn((s) => ({ ...s, [p.plan]: !s[p.plan] }))}
                  className="mt-3 w-full flex items-center justify-between brutal-sm rounded-xl bg-white px-3 py-2.5">
                  <span className="flex items-center gap-2 text-sm font-bold"><Users className="w-4 h-4" strokeWidth={2.5} />{t("Family Bundel")} ({p.family_seats} {t("personen")})</span>
                  <span className={`w-11 h-6 rounded-full relative transition-colors ${fam ? "bg-brand-violet" : "bg-brand-ink/20"}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${fam ? "left-5" : "left-0.5"}`} />
                  </span>
                </button>
              </BrutalCard>
            );
          })}
          <p className="text-xs text-brand-ink/50 text-center font-semibold">{t("Veilig betalen via Stripe. Test met kaart 4242 4242 4242 4242.")}</p>
        </div>
      )}
    </div>
  );
}
